/* section-article.js. Ratgeber article page enhancements.
 *
 * Loaded on article template only (gated by {%- if template.name == 'article' -%} in layout/theme.liquid).
 * Three concerns consolidated in one file:
 *   1. buildToC()    Auto-generates ToC from .art-body h2 headings. Respects existing IDs. German-umlaut slugify.
 *   2. setupToC()    IntersectionObserver toggles .active on .art-toc-desktop ol a as sections scroll.
 *   3. appendUTMs()  Decorates /products/ links inside .art-body, .closing-cta, .rel-prod with UTM campaign params.
 *
 * Dependencies: DOM from sections/main-article.liquid (Plan 03). document.body dataset reads article handle.
 *
 * Related decisions: D-08 (mobile ToC), D-09 (desktop ToC sticky), D-11 (delegated UTM JS; sole UTM owner per Plan 03 WARNING 4 fix), D-13 (.inline-prod Phase 7 fetch; this file only decorates existing anchors).
 *
 * BLOG-10 deferral: GA4 event firing (blog_related_product_click, blog_closing_cta_click) lands in appendUTMs() when FOUND-06 cookie-banner audit resolves. Hook point flagged in that function.
 */

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function buildToC() {
  var headings = document.querySelectorAll('.art-body h2');
  var desktopOl = document.querySelector('.art-toc-desktop ol');
  var mobileOl = document.querySelector('.art-toc-mobile ol');

  if (headings.length === 0) {
    // No H2s: hide both ToC containers so the empty "Inhalt" heading does not show
    var deskWrap = document.querySelector('.art-toc-desktop');
    var mobWrap = document.querySelector('.art-toc-mobile');
    if (deskWrap) deskWrap.style.display = 'none';
    if (mobWrap) mobWrap.style.display = 'none';
    return [];
  }
  var seenSlugs = {};
  var items = [];

  headings.forEach(function (h) {
    // Do NOT overwrite IDs already present in article body HTML (Pitfall 3)
    if (!h.id) {
      var slug = slugify((h.textContent || '').trim());
      if (!slug) slug = 'abschnitt';
      if (seenSlugs[slug]) {
        seenSlugs[slug]++;
        slug = slug + '-' + seenSlugs[slug];
      } else {
        seenSlugs[slug] = 1;
      }
      h.id = slug;
    } else {
      // Track existing IDs to avoid later collisions
      seenSlugs[h.id] = seenSlugs[h.id] ? seenSlugs[h.id] + 1 : 1;
    }
    items.push({ id: h.id, text: (h.textContent || '').trim() });
  });

  function appendTo(ol) {
    if (!ol) return;
    items.forEach(function (item) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + item.id;
      a.textContent = item.text;
      li.appendChild(a);
      ol.appendChild(li);
    });
  }

  appendTo(desktopOl);
  appendTo(mobileOl);
  return items;
}

function setupToC() {
  var items = buildToC();
  if (items.length === 0) return;
  // Graceful degradation if IO unsupported
  if (typeof IntersectionObserver === 'undefined') return;

  var desktopLinks = document.querySelectorAll('.art-toc-desktop ol a');
  if (!desktopLinks.length) return;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        desktopLinks.forEach(function (l) { l.classList.remove('active'); });
        var activeSelector = '.art-toc-desktop ol a[href="#' + entry.target.id + '"]';
        var active = document.querySelector(activeSelector);
        if (active) active.classList.add('active');
      }
    });
  }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });

  items.forEach(function (item) {
    var h = document.getElementById(item.id);
    if (h) observer.observe(h);
  });
}

function appendUTMs() {
  /* BLOG-10 HOOK POINT:
   * When the FOUND-06 cookie-banner audit resolves, GA4 event firing lands here:
   *   - Dispatch `blog_related_product_click` when a .rel-prod a[href*="/products/"] is clicked
   *   - Dispatch `blog_closing_cta_click` when a .closing-cta a is clicked
   *   - Dispatch `blog_related_product_view` via IntersectionObserver on .rel-prod .pc cards
   * All three events already have handle + article + position data available in this scope.
   * Per BLOG-10 deferral note in .planning/REQUIREMENTS.md: Phase 1 ships zero GA4 wiring.
   */

  var articleHandle = (document.body.dataset.articleHandle) || '';

  function decorateLink(a, content) {
    try {
      var url = new URL(a.href, window.location.origin);
      // Only decorate internal product URLs (/products/<handle>); skip external + non-product links
      if (url.pathname.indexOf('/products/') !== 0) return;
      // Idempotent: skip already-decorated anchors (prevents double-append on re-run)
      if (url.searchParams.has('utm_source')) return;
      url.searchParams.set('utm_source', 'blog');
      url.searchParams.set('utm_medium', 'internal');
      url.searchParams.set('utm_campaign', articleHandle);
      url.searchParams.set('utm_content', content);
      a.href = url.pathname + '?' + url.searchParams.toString() + url.hash;
    } catch (e) {
      // Invalid href, skip silently
    }
  }

  // Closing CTA (Plan 03 renders a plain href here; Plan 04 is the sole UTM owner)
  document.querySelectorAll('.closing-cta a[href*="/products/"]').forEach(function (a) {
    decorateLink(a, 'closing_cta');
  });

  // Related-products strip
  document.querySelectorAll('.rel-prod a[href*="/products/"]').forEach(function (a) {
    decorateLink(a, 'related_strip');
  });

  // Article body: inline product cards vs deep-link anchors
  document.querySelectorAll('.art-body a[href*="/products/"]').forEach(function (a) {
    var position = a.closest('.inline-prod') ? 'inline_card' : 'deep_link';
    decorateLink(a, position);
  });
}

function placeLeadFig() {
  /* Article feature image renders as a direct child of .art-body (before
     article.content) via main-article.liquid. Move it to sit after the
     intro paragraphs and before the first H2 section, matching where the
     claude-design mockup puts its hero figure. If the article has no
     H2 (short FAQ-style posts), keep the fig at the top of body. */
  var fig = document.querySelector('.art-body > .art-lead-fig');
  if (!fig) return;
  var firstH2 = document.querySelector('.art-body > h2');
  if (firstH2) {
    // Place before the first H2 (after whatever intro paragraphs/HR precede it)
    firstH2.parentNode.insertBefore(fig, firstH2);
    // If the element immediately before the fig is an <hr> separator, remove
    // the separator — the fig itself becomes the visual break between intro
    // and first section.
    var prev = fig.previousElementSibling;
    if (prev && prev.tagName === 'HR') prev.remove();
  }
}

(function () {
  // Early bail on non-article pages (belt-and-suspenders; layout/theme.liquid also gates script load)
  if (!document.querySelector('.art-body')) return;

  function init() {
    placeLeadFig();
    setupToC();
    // appendUTMs decorates /products/ links with blog-attribution UTMs.
    // Only run on actual articles — ported pages reuse this file solely
    // for the TOC builder and have no article handle for utm_campaign.
    if (document.body.dataset.articleHandle) appendUTMs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
